import React, {useEffect, useState} from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  Tooltip,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import {
  IGenericType,
  IPolygonInput,
  IPolygonSoilDataInput,
  IProduce,
} from 'constants/interfaces';
import {
  apiGetProduce,
  apiSavePolygons,
  apiGetIrrigationTypes,
} from 'constants/Api';
// eslint-disable-next-line max-len
import {AddNewFieldSoilData} from 'screens/Dashboard/Customers/CustomerDetail/CustomerDashboardMapping/components/AddNewFieldSoilData';

interface IAddNewFieldFormProps {
  onClose: () => void;
  drawnPolygon: {size: number; coordinates: Array<{lat: number; lng: number}>};
  farmId: number;
  onRemoveDraftPolygon?: () => void;
}

export const AddNewFieldForm: React.FC<IAddNewFieldFormProps> = ({
  onClose,
  drawnPolygon,
  farmId,
  onRemoveDraftPolygon,
}) => {
  // Full produce list and variety
  const [produce, setProduce] = useState<IProduce[]>([]);
  const [varieties, setVarieties] = useState<IGenericType[]>([]);
  const [selectedProduce, setSelectedProduce] = useState<IProduce | null>(null);

  // Step toggle for soil data
  const [soilDataStep, setSoilDataStep] = useState(false);

  // The soil data (if any) that the user has added
  const [soilData, setSoilData] = useState<IPolygonSoilDataInput | null>(null);

  const [irrigationOptions, setIrrigationOptions] = useState<string[]>([
    'None',
  ]);

  // The main form fields
  const [form, setForm] = useState({
    name: '',
    crop: '', // <-- We'll store the produce ID as a string
    variety: '', // <-- We'll store the variety ID as a string
    plantingDate: '',
    population: '',
    irrigation: '',
    yield: '',
    buyer: '',
    funder: '',
  });

  useEffect(() => {
    apiGetProduce().then((res) => {
      setProduce(res);
    });
    apiGetIrrigationTypes()
      .then((res) => {
        setIrrigationOptions([
          'None',
          ...res.map((r) => (r.name ? String(r.name) : '')),
        ]);
      })
      .catch(console.error);
  }, []);

  // ========== HANDLERS FOR PRODUCE / VARIETY ==========
  const handleProduceChange = (e: React.ChangeEvent<{value: unknown}>) => {
    // Find the produce by ID
    const selected = produce.find((p) => p.id === e.target.value);
    setSelectedProduce(selected || null);
    setVarieties(selected?.variety || []);

    // Store the ID in form.crop (so MUI can match the <MenuItem value>)
    setForm((prev) => ({...prev, crop: String(selected?.id || '')}));
  };

  const handleVarietyChange = (e: React.ChangeEvent<{value: unknown}>) => {
    // Find the variety by ID
    const found = varieties.find((v) => v.id === e.target.value);
    // Store the ID in form.variety
    setForm((prev) => ({...prev, variety: String(found?.id || '')}));
  };

  // For other text fields & string-based selects (e.g. irrigation)
  const handleChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({...prev, [field]: e.target.value}));
    };

  // Basic required check
  const isFormValid = (): boolean => {
    const {
      name,
      crop,
      plantingDate,
      population,
      irrigation,
      yield: yieldVal,
    } = form;

    return String(name).trim() &&
      String(crop).trim() &&
      String(plantingDate).trim() &&
      String(population).trim() &&
      String(irrigation).trim() &&
      String(yieldVal).trim()
      ? true
      : false;
  };

  // Save to backend
  const handleSavePolygon = async () => {
    if (!isFormValid()) return;

    // If you need the actual produceId as a number:
    const produceId = parseInt(form.crop, 10) || selectedProduce?.id;

    // If you need the variety name or ID:
    const varietyObj = varieties.find(
      (v) => v.id === parseInt(form.variety, 10),
    );
    const varietyName = varietyObj?.name || '';

    const polygonData: IPolygonInput = {
      info: {
        fieldName: form.name,
        hectares: drawnPolygon.size,
        produceId: produceId,
        variety: varietyName, // or store the numeric ID if that’s how your backend expects it
        plantedOn: form.plantingDate,
        plantPopulation: parseFloat(form.population),
        irrigation: form.irrigation,
        expectedYield: parseFloat(form.yield),
      },
      coordinates: drawnPolygon.coordinates,
      soilData: soilData
        ? {
            soilType: soilData.soilType,
            pH: parseFloat(soilData.pH?.toString() || '0'),
            P: parseFloat(soilData.P?.toString() || '0'),
            K: parseFloat(soilData.K?.toString() || '0'),
            Ca: parseFloat(soilData.Ca?.toString() || '0'),
            Mg: parseFloat(soilData.Mg?.toString() || '0'),
            Na: parseFloat(soilData.Na?.toString() || '0'),
            cations: parseFloat(soilData.cations?.toString() || '0'),
            exchangeableAcidity: parseFloat(
              soilData.exchangeableAcidity?.toString() || '0',
            ),
          }
        : undefined,
    };

    try {
      await apiSavePolygons(polygonData, farmId);
      onRemoveDraftPolygon?.();
      handleClear();
      onClose();
    } catch (err) {
      console.error('Error saving polygon:', err);
    }
  };

  const handleClear = () => {
    setForm({
      name: '',
      crop: '',
      variety: '',
      plantingDate: '',
      population: '',
      irrigation: '',
      yield: '',
      buyer: '',
      funder: '',
    });
    setSelectedProduce(null);
    setVarieties([]);
    setSoilData(null);
    setSoilDataStep(false);
  };

  if (soilDataStep) {
    return (
      <AddNewFieldSoilData
        initialSoilData={soilData || {}}
        onSubmit={(data) => {
          setSoilData({
            soilType: data.soilType,
            pH: data.pH,
            P: data.P,
            K: data.K,
            Ca: data.Ca,
            Mg: data.Mg,
            Na: data.Na,
            cations: data.cations,
            exchangeableAcidity: data.exchangeableAcidity,
          });
          setSoilDataStep(false);
        }}
        onCancel={() => setSoilDataStep(false)}
      />
    );
  }

  return (
    <Box
      p={3}
      borderRadius={12}
      boxShadow={1}
      bgcolor="#FAFAFA"
      maxWidth={800}
      margin="auto"
      style={{
        maxHeight: '80vh',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6" style={{fontWeight: 600}}>
          Add new field
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Fields */}
      {[
        {label: 'Field name', key: 'name', placeholder: 'Enter name'},
        {
          // ========== Crop Planted (produce) ==========
          label: 'Crop planted',
          key: 'crop',
          placeholder: 'Select crop',
          select: true,
          // We'll store the produce ID in form.crop
          // Then for menu items, we pass value={p.id} and display p.name
          options: produce,
          onChange: handleProduceChange,
        },
        {
          // ========== Variety Planted ==========
          label: (
            <Box display="flex" alignItems="center">
              Variety planted
              <Tooltip title="Type of crop variety">
                <HelpOutlineIcon fontSize="small" style={{marginLeft: 4}} />
              </Tooltip>
            </Box>
          ),
          key: 'variety',
          placeholder: 'Select variety',
          select: true,
          options: varieties,
          onChange: handleVarietyChange,
        },
        {label: 'Planting date', key: 'plantingDate', type: 'date'},
        {
          label: 'Plant population',
          key: 'population',
          placeholder: 'Enter population',
        },
        {
          label: 'Irrigation',
          key: 'irrigation',
          select: true,
          options: irrigationOptions,
        },
        {
          label: 'Expected yield',
          key: 'yield',
          placeholder: 'Enter yield',
        },
        {
          label: 'Buyer',
          key: 'buyer',
          select: true,
          options: ['Buyer A', 'Buyer B'],
        },
        {
          label: 'Funder',
          key: 'funder',
          select: true,
          options: ['Funder A', 'Funder B'],
        },
      ].map(({label, key, placeholder, type, select, options, onChange}) => (
        <Box key={key} mb={2}>
          <Typography style={{fontSize: 14, fontWeight: 500, marginBottom: 4}}>
            {label}
          </Typography>
          <TextField
            variant="outlined"
            placeholder={placeholder}
            type={type || 'text'}
            value={(form as any)[key]}
            onChange={onChange || handleChange(key)}
            fullWidth
            size="small"
            select={Boolean(select)}
            InputProps={{style: {backgroundColor: '#fff', borderRadius: 8}}}
          >
            {select &&
              // If it's the "crop" or "variety" array => objects with { id, name }
              // If it's "irrigation"/"buyer"/"funder" => strings
              options?.map((opt: any) => {
                // If it's an object with .id => use that for the <MenuItem> value
                if (opt.id) {
                  return (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name}
                    </MenuItem>
                  );
                }
                // Otherwise it's a plain string
                return (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                );
              })}
          </TextField>
        </Box>
      ))}

      {/* Buttons */}
      <Box
        mt={4}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Button
          onClick={handleClear}
          style={{color: '#2D9B73', fontWeight: 600}}
        >
          Clear All
        </Button>

        <Box display="flex">
          <Button
            onClick={() => setSoilDataStep(true)}
            variant="outlined"
            style={{
              borderRadius: 24,
              padding: '6px 18px',
              borderColor: '#ccc',
              backgroundColor: '#f9f9f9',
            }}
          >
            Add soil data
          </Button>

          <Button
            onClick={handleSavePolygon}
            variant="contained"
            style={{
              borderRadius: 24,
              padding: '6px 18px',
              backgroundColor: isFormValid() ? '#2D9B73' : '#ccc',
              color: '#fff',
              fontWeight: 600,
            }}
            disabled={!isFormValid()}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
